import { action, observable } from 'mobx';
import { FetchOptions, SerializedData } from 'bernie-core';
import { AnalyticsStore } from 'bernie-plugin-mobx';
import { Logger, NOOP_LOGGER, SystemEvent, SystemEventLevel } from 'bernie-logger';

import {
  FlightsFaresSeparationType,
  FlightsQueryState,
  FlightsShoppingPwaFlightSearchResultsFlightsSearch,
  FlightsShoppingPwaFlightSearchResultsGreedyFetchLoading,
  FlightsShoppingPwaFlightSearchResultsLoadingFlightsSearch,
  Sdk
} from '__generated__/api/';
import analyticsWrapper from 'utility/analytics-wrapper';
import { getFlightsSearchLoadedHeaders } from 'utility/header-utils';
import { BaseBexApiStore } from './bex-api-store';

declare global {
  interface Window {
    searchResultPromise: Promise<Response>;
  }
}

export class SearchResultsStore extends BaseBexApiStore {
  @observable public isSubsequentialSearch: boolean;
  @observable public flightsSearchResultLoadedRequest: RequestInit;
  @observable public unknownErrorIdentified: boolean;
  @observable public isInitialSearch: boolean;

  public hydrate(data: SerializedData): void {
    super.hydrate(data);
  }

  constructor(state: SerializedData = {}, logger: Logger = NOOP_LOGGER, api: Sdk) {
    super(state, logger, api);
  }

  private handleResponse(
    response:
      | FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsSearch
      | FlightsShoppingPwaFlightSearchResultsGreedyFetchLoading.FlightsSearch,
    searchVariables: FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables,
    fetchType: string
  ) {
    this.logger.logEvent(
      new SystemEvent(SystemEventLevel.INFO, `FSR_FETCH_${fetchType}_VARIABLES`),
      JSON.stringify(searchVariables)
    );
    this.logger.logEvent(
      new SystemEvent(SystemEventLevel.INFO, `FSR_FETCH_${fetchType}_RESPONSE`),
      JSON.stringify(response)
    );

    return {
      ...response
    };
  }

  public fetchOnSearchClick(
    searchVariables: FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables,
    options: HeadersInit
  ): Promise<FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsSearch> {
    searchVariables.queryState = FlightsQueryState.LOADING;

    return this.api
      .FlightSearchResultsOnClientLoading(searchVariables, options)
      .then(response => {
        return this.handleResponse(response.data.flightsSearch, searchVariables, 'SUBSEQUENTIAL_SEARCH');
      })
      .catch(error => {
        this.logger.logEvent(new SystemEvent(SystemEventLevel.CRITICAL, 'FETCH_SUBSEQUENTIAL_SEARCH'), error);

        return null;
      });
  }

  public fetchLoading(
    searchVariables: FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables,
    options: HeadersInit,
    analytics: AnalyticsStore,
    isGreedyFetchLoadingQuery?: boolean
  ): Promise<FlightsShoppingPwaFlightSearchResultsLoadingFlightsSearch.FlightsSearch> {
    const operationName = isGreedyFetchLoadingQuery
      ? 'FlightsShoppingPwaFlightSearchResultsGreedyFetchLoading'
      : 'FlightsShoppingPwaFlightSearchResultsLoadingFlightsSearch';

    return this.api[operationName](searchVariables, options)
      .then(response => {
        analyticsWrapper.publishAnalyticsPayload(response, analytics);

        return this.handleResponse(response.data.flightsSearch, searchVariables, 'LOADING');
      })
      .catch(error => {
        this.logger.logEvent(new SystemEvent(SystemEventLevel.CRITICAL, 'FETCH_FSR_LOADING'), error);

        return null;
      });
  }

  public async fetchResults(
    searchVariables: FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables,
    options: HeadersInit,
    analytics: AnalyticsStore,
    initialListingsFetch = true,
    hasNonStopListingError = false
  ): Promise<FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsSearch> {
    try {
      let flightsSearchResultLoadedResponseData;
      if (initialListingsFetch && window.searchResultPromise) {
        const flightsSearchResultLoadedResponse = await window.searchResultPromise;
        flightsSearchResultLoadedResponseData = await flightsSearchResultLoadedResponse.clone().json();
        window.searchResultPromise = null;
        this.setFlightsSearchResultLoadedRequest();
      } else {
        if (
          hasNonStopListingError &&
          (searchVariables.faresSeparationType === FlightsFaresSeparationType.NONSTOP_ONLY_BASE ||
            searchVariables.faresSeparationType === FlightsFaresSeparationType.NONSTOP_ONLY_UPSELL ||
            searchVariables.faresSeparationType === FlightsFaresSeparationType.NONSTOP_BASE_AND_UPSELL)
        ) {
          return;
        } else if (
          searchVariables.faresSeparationType === FlightsFaresSeparationType.ONLY_UPSELL ||
          searchVariables.faresSeparationType === FlightsFaresSeparationType.NONSTOP_ONLY_UPSELL
        ) {
          flightsSearchResultLoadedResponseData = await this.api.FlightsShoppingPwaFlightSearchUpsellResultsFlightsSearch(
            searchVariables,
            options
          );
        } else {
          flightsSearchResultLoadedResponseData = await this.api.FlightsShoppingPwaFlightSearchResultsFlightsSearch(
            searchVariables,
            options
          );
        }
      }

      const { flightsSearch } = flightsSearchResultLoadedResponseData.data;
      if (
        initialListingsFetch ||
        (hasNonStopListingError && searchVariables.faresSeparationType === FlightsFaresSeparationType.ONLY_BASE)
      ) {
        analyticsWrapper.setSearchResultsAnalytics(flightsSearchResultLoadedResponseData);
        analyticsWrapper.publishSearchAnalyticsPayload(analytics);
      }

      return {
        ...flightsSearch
      };
    } catch (error) {
      this.logger.logEvent(new SystemEvent(SystemEventLevel.CRITICAL, 'FETCH_FSR_LOADED_FAILED'), error);
      this.unknownErrorIdentified = true;

      return null;
    }
  }

  @action
  public setIsSubsequentialSearch = (isSubsequentialSearch?: boolean) => {
    this.isSubsequentialSearch = isSubsequentialSearch;
  };

  @action
  public setIsInitialSearch = (isInitialSearch?: boolean) => {
    this.isInitialSearch = isInitialSearch;
  };

  @action
  public setFlightsSearchResultLoadedRequest = (flightsSearchResultLoadedRequest?: RequestInit) => {
    this.flightsSearchResultLoadedRequest = flightsSearchResultLoadedRequest;
  };

  public createFlightsSearchLoadedRequest(
    options: FetchOptions,
    variables: FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables,
    pageId: string
  ): RequestInit {
    return getFlightsSearchLoadedHeaders(options, variables, 'flights-shopping-pwa', 'external', pageId);
  }
}

SearchResultsStore.DEFAULTS = {
  unknownErrorIdentified: false
};
